import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Check } from "lucide-react";
import { memo, useCallback, useMemo, useState } from "react";
import { AppModules, PERMISSIONS, PermissionValues } from "./constants";

// Separate the module button into its own component for better reusability and performance
const ModuleButton = memo(
  ({
    module,
    isSelected,
    permissions,
    onSelect,
  }: {
    module: string;
    isSelected: boolean;
    permissions: PermissionValues[];
    onSelect: (module: string) => void;
  }) => {
    const modulePermissionCount = useMemo(
      () =>
        permissions.filter((p) =>
          p.includes(AppModules[module as keyof typeof AppModules])
        ).length,
      [permissions, module]
    );

    const modulePermissionsLength = useMemo(
      () =>
        Object.values(
          PERMISSIONS[module as keyof typeof PERMISSIONS].permissions
        ).length,
      [module]
    );

    return (
      <Button
        variant={isSelected ? "secondary" : "ghost"}
        type="button"
        className="justify-start"
        onClick={() => onSelect(module)}
      >
        <span className="flex items-center gap-2 w-full justify-between">
          <span className="text-sm capitalize">
            {module.toLowerCase().replace(/_/g, " ")}
          </span>
          {modulePermissionCount > 0 ? (
            modulePermissionsLength === modulePermissionCount ? (
              <Badge variant="outline" className="py-0.5">
                <Check className="!size-3" />
              </Badge>
            ) : (
              <Badge variant="outline">{modulePermissionCount}</Badge>
            )
          ) : null}
        </span>
      </Button>
    );
  }
);
ModuleButton.displayName = "ModuleButton";

// Separate permission switch into its own component
const PermissionSwitch = memo(
  ({
    permissionKey,
    permissionValue,
    isChecked,
    onToggle,
  }: {
    permissionKey: string;
    permissionValue: PermissionValues;
    isChecked: boolean;
    onToggle: (value: PermissionValues, checked: boolean) => void;
  }) => (
    <Label className="flex items-center gap-2">
      <Switch
        checked={isChecked}
        onCheckedChange={(checked) => onToggle(permissionValue, checked)}
      />
      <span className="text-sm capitalize font-normal">
        {permissionKey.toLowerCase().replace(/_/g, " ")}
      </span>
    </Label>
  )
);
PermissionSwitch.displayName = "PermissionSwitch";

interface PermissionInputProps {
  value: PermissionValues[];
  onChange: (value: PermissionValues[]) => void;
}

const PermissionInput = ({ value, onChange }: PermissionInputProps) => {
  const [selectedModule, setSelectedModule] =
    useState<keyof typeof AppModules>("EMAIL_HEALTH");

  const modulePermissions = useMemo(
    () => Object.values(PERMISSIONS[selectedModule].permissions),
    [selectedModule]
  );

  const isModuleFullySelected = useMemo(
    () => modulePermissions.every((permission) => value?.includes(permission)),
    [modulePermissions, value]
  );

  const handlePermissionToggle = useCallback(
    (permissionValue: PermissionValues, checked: boolean) => {
      onChange(
        checked
          ? [...value, permissionValue]
          : value.filter((p) => p !== permissionValue)
      );
    },
    [value, onChange]
  );

  const handleModuleToggle = useCallback(
    (checked: boolean) => {
      onChange(
        checked
          ? [...new Set([...value, ...modulePermissions])]
          : value.filter((p) => !modulePermissions.includes(p))
      );
    },
    [value, modulePermissions, onChange]
  );

  const handleModuleSelect = useCallback((module: string) => {
    setSelectedModule(module as keyof typeof AppModules);
  }, []);

  return (
    <Card className="grid grid-cols-[150px_1fr] md:grid-cols-[200px_1fr] gap-4 p-3 bg-transparent">
      <div className="flex flex-col gap-2 border-r border-border">
        <h3 className="text-sm font-medium">Modules</h3>
        <ScrollArea className="h-[300px] pr-4">
          <div className="flex flex-col gap-2">
            {Object.keys(AppModules).map((module) => (
              <ModuleButton
                key={module}
                module={module}
                isSelected={selectedModule === module}
                permissions={value}
                onSelect={handleModuleSelect}
              />
            ))}
          </div>
        </ScrollArea>
      </div>

      <div className="flex flex-col gap-4">
        <Label>Permissions</Label>
        <ScrollArea className="h-[300px]">
          {selectedModule && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Label className="flex items-center gap-2 col-span-full">
                <Switch
                  checked={isModuleFullySelected}
                  onCheckedChange={handleModuleToggle}
                />
                <span className="text-sm font-normal">All</span>
              </Label>

              {Object.entries(PERMISSIONS[selectedModule].permissions).map(
                ([key, permissionValue]) => (
                  <PermissionSwitch
                    key={permissionValue}
                    permissionKey={key}
                    permissionValue={permissionValue}
                    isChecked={value?.includes(permissionValue) || false}
                    onToggle={handlePermissionToggle}
                  />
                )
              )}
            </div>
          )}
        </ScrollArea>
      </div>
    </Card>
  );
};

// Export as memo to prevent unnecessary re-renders from parent
export const MemoizedPermissionInput = memo(PermissionInput);
